import { Box, Flex } from '@atlaskit/primitives';
import { messages } from '@databases/intl/messages';
import { logError, logWarning } from '@databases/logs';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import {
  type BrokenDatabase,
  type Database,
  type DatabaseFieldType,
  type EntryWithContext
} from '@databases/types/database';
import { getErrStatus } from '@databases/utils/confluence/fetch';
import { isNonEmptyEntryLinkValue } from '@databases/utils/database/field-types/entryLink';
import {
  getDatabaseEntryOptional,
  getEntryName,
  isNonBrokenDatabase
} from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByIdOptional
} from '@databases/utils/redux/databases/selectors';
import React, { type FC, useRef, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { PopoverEdit } from '../PopoverEdit';
import { usePopoverSoftSelection } from '../popover-edit-common/usePopoverSoftSelection';
import { type EditComponentProps } from '../types';

import EntryLinkSelect from './EntryLinkSelect';
import { EntryLinkValue } from './EntryLinkValue';
import type { EntryLinkReference } from './types';

const EntryLinkPopoverEdit: FC<EditComponentProps<DatabaseFieldType.ENTRY_LINK>> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  entryId,
  contentId,
  isRenderedInSidePanel,
  isBulkEdit,
  ariaLabel,
  initialKey
}) => {
  const {
    databaseSpace,
    databaseId,
    contentId: databaseContentId,
    multi: fieldSettingsIsMulti
  } = field.settings;
  const hasCurrentData = value.length > 0;
  const addDataSectionRef = useRef<HTMLDivElement | null>(null);
  const currentDataSectionRef = useRef<HTMLDivElement | null>(null);
  const intl = useIntl();
  const { formatMessage } = intl;
  const dispatch = defaultStoreUtils.useDispatch();
  usePopoverSoftSelection({
    entryId,
    fieldId: field.id,
    isRenderedInSidePanel
  });

  useCallbackEffect(
    useCallback(async () => {
      try {
        if (databaseContentId) {
          await dispatch(loadDatabaseByContentId(databaseContentId));
        }
      } catch (err: unknown) {
        const error = new Error(
          `Could not load database with content id ${databaseContentId}: ${err}`
        );
        const status = getErrStatus(err);
        if (status && (status === 403 || status === 404)) {
          logWarning(error);
        } else {
          logError(error);
        }
      }
    }, [databaseContentId, dispatch])
  );

  const entryValues: EntryLinkReference[] = defaultStoreUtils.useEqualSelector(
    (state) => {
      if ((!databaseSpace || !databaseId) && !databaseContentId) {
        return [];
      }
      let targetDatabase: Database | BrokenDatabase | undefined = undefined;

      if (databaseId) {
        targetDatabase = selectDatabaseByIdOptional(state, {
          spaceKey: databaseSpace?.key,
          id: databaseId
        });
      } else if (databaseContentId) {
        targetDatabase = selectDatabaseByContentIdOptional(state, {
          contentId: databaseContentId
        });
      }

      if (!targetDatabase) {
        return value.filter(isNonEmptyEntryLinkValue).flatMap(({ entryId, _id }) => [
          {
            entryId,
            title: formatMessage(messages['broken-link.broken-link-text']),
            _id
          }
        ]);
      }

      const targetDatabaseDraft =
        targetDatabase &&
        isNonBrokenDatabase(targetDatabase) &&
        selectDatabaseDraftByContentId(state, {
          contentId: targetDatabase.contentId,
          includeDeleted: false
        });
      if (!targetDatabaseDraft) {
        return [];
      } else {
        return value.filter(isNonEmptyEntryLinkValue).flatMap(({ entryId, _id }) => {
          const entry = getDatabaseEntryOptional(targetDatabaseDraft, entryId);
          const getTitle = (entryWithContext: EntryWithContext) => {
            const entryName = getEntryName(entryWithContext, state);

            return entryName
              ? entryName
              : formatMessage(messages['unnamed-entities.unnamed-entry']);
          };

          return entry
            ? [
                {
                  databaseSpaceKey: databaseSpace?.key,
                  databaseId: databaseId,
                  entryId,
                  title: getTitle({ database: targetDatabaseDraft, entry }),
                  _id,
                  contentId: databaseContentId
                }
              ]
            : [
                {
                  entryId,
                  title: formatMessage(messages['broken-link.broken-link-text']),
                  _id
                }
              ];
        });
      }
    }
  );

  const isMulti = fieldSettingsIsMulti || entryValues.length > 1;

  const EntryLinkAddDataSection = useMemo(
    () => (
      <div data-testid="entry-link-add-data-section" ref={addDataSectionRef}>
        <EntryLinkSelect
          value={value}
          field={field}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={autoFocus}
          isRenderedInSidePanel={isRenderedInSidePanel}
          addDataSectionRef={addDataSectionRef}
          aria-label={ariaLabel}
          initialKey={initialKey}
        />
      </div>
    ),
    [
      value,
      field,
      onChange,
      onFocus,
      onBlur,
      autoFocus,
      isRenderedInSidePanel,
      ariaLabel,
      initialKey
    ]
  );

  const EntryLinkCurrentDataSection = useMemo(
    () => (
      <div data-testid="entry-link-current-data-section" ref={currentDataSectionRef}>
        <Flex direction="column">
          {entryValues.map((entryVal) => (
            <Box key={entryVal._id}>
              <EntryLinkValue
                contentId={contentId}
                entryLinkValue={entryVal}
                isReadOnly
                autoFocus={false}
                field={field}
                value={value}
                onChange={onChange}
                addDataSectionRef={addDataSectionRef}
              />
            </Box>
          ))}
        </Flex>
      </div>
    ),
    [entryValues, contentId, field, value, onChange]
  );

  return (
    <PopoverEdit
      isMultiSelect={isMulti}
      hasCurrentData={hasCurrentData}
      currentDataSection={EntryLinkCurrentDataSection}
      addDataSection={EntryLinkAddDataSection}
      isRenderedInSidePanel={isRenderedInSidePanel}
      fieldWidth={field.width}
      isBulkEdit={isBulkEdit}
    />
  );
};

export default EntryLinkPopoverEdit;
